import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styled } from '@mui/system';
import TextField from '@mui/material/TextField';

import LeftSide from '@/components/LeftSide';
import RightSide from '@/components/RightSize';
import Tabs from '@/components/Tabs';
import Panel from '@/components/Panel';
import GearIcon from '@/components/icons/Gear';
import CrossIcon from '@/components/icons/Cross';
import PersonIcon from '@/components/icons/Person';

import { RootState, useAppSelector } from '@/state/store';
import { PASSWORD_RESET_WAIT_TIMEOUT } from '@/state/authentication.slice';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  setProfile,
  setAvatar,
  deleteAvatar,
  deleteProfile,
  startPasswordReset,
  checkPasswordResetClaim,
} from '@/actions/profile';
import { exitPoint as setExitPoint } from '@/actions/common';
import { checkEmailVerification } from '@/loaders/user';
import {
  // AccessSelector,
  Button,
  ImageField
} from '@/components';
import { showMessage } from '@/actions/common';
import { createSelector } from '@reduxjs/toolkit';
import PanelButton from '@/components/PanelButton';
import PageTitle from '@/components/PageTitle';
import t from '@/utils/translate';
import { useFormik } from 'formik';
import { ProfileUpdateRequest } from '@bimdata/definitions/dto';
import Prompt from '@/components/Prompt';
import isEmailAddressValid from '@/utils/isEmailAddressValid';

// @ts-ignore: Unreachable code error
const AVA_SIZE_LIMIT = +import.meta.env.VITE_AVA_SIZE_LIMIT_KB || 512;

const TopRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 20px;
`;

const BottomRow = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '32px',
  padding: '20px 0',
  flexGrow: 1,
});

const CustomButton = styled(Button)({
  width: '160px',
  height: '32px',
  fontSize: '12px',
  fontWeight: 500,
  padding: '9px',
  borderRadius: '6px',
});

const SubmitButton = styled(CustomButton)({
  '&:not(.Mui-disabled)': {
    border: '1px solid #0747A6'
  }
});

const DeleteButton = styled(CustomButton)({
  border: '1px solid #C1C7D0',
  color: '#FF8F73',
});

const PwdResetButton = styled(CustomButton)({
  width: '100%',
  border: '1px solid #C1C7D0',
  color: '#2684FF',
  '&:disabled': {
    background: '#FAFAFA',
    color: '#C1C7D0'
  }
});

const HalfBlock = styled('div')({
  width: '50%',
  padding: '20px',
  display: 'flex',
  justifyContent: 'center',
});

const TitleText = styled('span')`
  font-size: 17px;
  font-weight: 500;
  padding-left: 2px;
`;

const FormMessage = styled('span')({
  display: 'inline-block',
  marginTop: '10px',
  color: '#D53B3B',
});

const ProfileForm = styled('form')({
  width: '400px',
  gap: '30px',
  display: 'flex',
  flexDirection: 'column'
});

type FormDataType = {
  avatarFile: File | null,
  avatarPath: string | null,
} & ProfileUpdateRequest;

export default () => {
  const {
    profile,
    emailVerificationStarted,
    passwordResetClaimId,
    passwordResetStarted,
    avatars,
    exitPoint,
  } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.authentication.profile,
        (state: RootState) => state.authentication.emailVerificationStarted,
        (state: RootState) => state.authentication.passwordResetClaimId,
        (state: RootState) => state.authentication.passwordResetStarted,
        (state: RootState) => state.user.avatars,
        (state: RootState) => state.common.exitPoint,
      ],
      (
        profile,
        emailVerificationStarted,
        passwordResetClaimId,
        passwordResetStarted,
        avatars,
        exitPoint,
      ) => ({
        profile,
        emailVerificationStarted,
        passwordResetClaimId,
        passwordResetStarted,
        avatars,
        exitPoint
      })
    )
  );

  const nav = useNavigate();
  const [profileRemoving, setProfileRemoving] = useState(false);
  const [checkEmailVerificationInterval, setCheckEmailVerificationInterval] = useState<number | null>(null);
  const [checkPwdResetInterval, setPwdResetInterval] = useState<number | null>(null);

  const [tab, setTab] = useState<string>('general');

  useEffect(() => {
    if (passwordResetClaimId && !checkPwdResetInterval) {
      const interval = window.setInterval(() => {
        checkPasswordResetClaim(passwordResetClaimId);
      }, 5000);
      setPwdResetInterval(interval);
      return;
    }
    if (checkPwdResetInterval && !passwordResetClaimId) {
      clearInterval(checkPwdResetInterval);
      setPwdResetInterval(null);
    }
  }, [ passwordResetClaimId, checkPwdResetInterval, passwordResetStarted ]);

  const close = useCallback(() => {
    if (exitPoint) {
      nav(exitPoint);
      setExitPoint(null);
    } else {
      nav('/');
    }
  }, [exitPoint]);

  const submitCallback = useCallback(
    async ({ avatarPath, avatarFile, ...profileData }: FormDataType): Promise<FormDataType> => {
      if (profile) {
        if (profileData.email) {
          profileData.email = profileData.email.toLowerCase();
        }
        const data = Object.entries(profileData).filter(
          ([key, value]) => (value !== ((profile as Record<string, any>)[key] || ''))
        );
        if (data.length) {
          await setProfile(
            data.reduce((acc, [key, value]) => ({
              ...acc,
              [key]: value
            }), {})
          );
        }

        profileData.email = profile.email;

        if (avatarPath !== (avatars[profile.id]?.source || null)) {
          if (!avatarFile) {
            await deleteAvatar();
          } else if (avatarFile.size > 1024 * AVA_SIZE_LIMIT) {
            showMessage(t('profile.error.avaSize', { limit: AVA_SIZE_LIMIT }), 'error');
            avatarPath = avatars[profile.id]?.source || null;
            avatarFile = null;
          } else {
            await setAvatar(avatarFile);
          }
        }
      }
      return { avatarFile, avatarPath, ...profileData };
    },
    [ profile, avatars ]
  );

  const formik = useFormik<FormDataType>({
    initialValues: {
      email: profile?.email || '',
      lastName: profile?.lastName || '',
      name: profile?.name || '',
      avatarPath: avatars[profile?.id || '']?.source || null,
      avatarFile: null,
    },
    validate: ({ email, phone, name, lastName, patronymic }) => {
      const errors: Record<string, string> = {};

      if (!email) {
        errors.email = t('profile.validation.email');
      } else if (!isEmailAddressValid(email)) {
        errors.email = t('profile.validation.invalidEmail')
      } else if (email.length > 128) {
        errors.email = t('profile.validation.tooLong', { limit: 128 });
      }

      for (const [key, value] of Object.entries({ phone, name, lastName, patronymic })) {
        if (value && value.length > 128) {
          errors[key] = t('profile.validation.tooLong', { limit: 128 });
        }
      }   

      return errors;
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, helpers) => {
      values = await submitCallback(values);
      helpers.resetForm({ values });
    }
  });

  useEffect(() => {
    if ((emailVerificationStarted || profile?.emailCandidate) && !checkEmailVerificationInterval) {
      const interval = window.setInterval(async () => {
        await checkEmailVerification();
      }, 10000);
      setCheckEmailVerificationInterval(interval);
      return;
    }
    if (!profile?.emailCandidate && !emailVerificationStarted && checkEmailVerificationInterval) {
      clearInterval(checkEmailVerificationInterval);
      setCheckEmailVerificationInterval(null);
      formik.setFieldValue('email', profile?.email);
    }
  }, [ emailVerificationStarted, profile?.emailCandidate, checkEmailVerificationInterval ]);  

  const onAvatarSetCallback = useCallback((preview: File) => {
    formik.setFieldValue('avatarFile', preview);
    formik.setFieldValue('avatarPath', URL.createObjectURL(preview));
  }, [ formik ]);

  const onAvatarClearCallback = useCallback(() => {
    formik.setFieldValue('avatarFile', null);
    formik.setFieldValue('avatarPath', null);
  }, [ formik ]);

  if (!profile) {
    return <Navigate to='/login' replace />
  }

  const specificFullName = useMemo(() => {
    if (!profile.name || !profile.lastName) {
      return profile.email;
    }
    return `${profile.name} ${profile.lastName[0]}. (${profile.email})`;
  }, [ profile ]);

  return (
    <>
      <PageTitle>{t('profile.title.main')} — BIMDATA</PageTitle>
      <Panel margin-h="medium" text-align="center">
        <LeftSide><GearIcon size={24} fill='#6B778C' style={{ margin: '6px' }} /><TitleText>{t('profile.title.settings')}</TitleText></LeftSide>
        <RightSide>
          <PanelButton onClick={close}>
            <CrossIcon size={18} />
          </PanelButton>
        </RightSide>
      </Panel>

      <Panel>
        <Tabs
          value={tab}
          tabs={[
            {
              key: 'general',
              title: t('profile.title.general'),
            },
            /*
            {
              key: 'security',
              title: t('profile.title.security'),
            },
            {
              key: 'invitations',
              title: t('profile.title.invitations'),

            },
            {
              key: 'misc',
              title: t('profile.title.misc'),
            }
            */
          ]}
          onTabSelect={setTab}
        />
      </Panel>

      <TopRow>

        <HalfBlock style={{ borderRight: '1px solid #DFE1E6' }}>
          <ImageField
            title={t('profile.title.avatar')}
            sizeH={320}
            sizeV={365}
            previewSizeV={238}
            style={{
              padding: '15px 39px',
              gap: '16px'
            }}
            placeholder={<PersonIcon size={60} fill='#EBECF0' style={{ verticalAlign: 'middle' }}/>}
            source={formik.values.avatarPath}
            onSet={onAvatarSetCallback}
            onClear={onAvatarClearCallback}
            variant='rounded'
          />
        </HalfBlock>

        <HalfBlock>
          <ProfileForm>
            <TextField
              {...formik.getFieldProps('name')}
              error={formik.touched.name && !!formik.errors.name}
              helperText={formik.touched.name && formik.errors.name}
              disabled={formik.isSubmitting}
              label={t('profile.label.name')}
              fullWidth
              margin="none"
              variant="outlined"
              size="small"
            />

            <TextField
              {...formik.getFieldProps('lastName')}
              error={formik.touched.lastName && !!formik.errors.lastName}
              helperText={formik.touched.lastName && formik.errors.lastName}
              disabled={formik.isSubmitting}
              label={t('profile.label.lastname')}
              fullWidth
              margin="none"
              variant="outlined"
              size="small"
            />

            {/*
            <TextField
              {...profileFormik.getFieldProps('patronymic')}
              error={profileFormik.touched.patronymic && !!profileFormik.errors.patronymic}
              helperText={profileFormik.touched.patronymic && profileFormik.errors.patronymic}
              disabled={profileFormik.isSubmitting}
              label={t('profile.label.patronymic')}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
            />
            */}

            <div>
              <TextField
                {...formik.getFieldProps('email')}
                error={formik.touched.email && !!formik.errors.email}
                helperText={
                  formik.touched.email && !!formik.errors.email
                  ? formik.errors.email
                  : ''
                    /*(
                      (!profile.phoneConfirmed || (profileFormik.values.phone !== profile.phone))
                      && t('profile.hint.pendingPhone')
                    )*/
                }
                disabled={formik.isSubmitting/* || !profile.phoneConfirmed || (profileFormik.values.phone !== profile.phone)*/}
                label={t('profile.label.email')}
                fullWidth
                margin="none"
                variant="outlined"
                size="small"
                onChange={(e) => {
                  formik.setFieldError('email', '');
                  formik.setFieldValue('email', e.target.value);
                }}
              />
              {profile.emailCandidate
                && (<FormMessage>{t('profile.message.emailConfirmationSent', { email: profile.emailCandidate }, true)}</FormMessage>)
              }
            </div>

            <div>
              <PwdResetButton
                type="button"
                variant="contrast-text"
                fullWidth
                color="primary"
                size="large"
                loading={formik.isSubmitting}
                onClick={() => startPasswordReset()}
                disabled={!!passwordResetClaimId}
              >
                {t('profile.action.pwdChange')}
              </PwdResetButton>
              {!!passwordResetClaimId
                && (<FormMessage>{t('profile.message.pwdLinkSent', { count: Math.round(PASSWORD_RESET_WAIT_TIMEOUT / 3600) }, true)}</FormMessage>)
              }
            </div>
          </ProfileForm>
        </HalfBlock>
      </TopRow>

      <BottomRow>
        <SubmitButton
          type="submit"
          disabled={formik.isSubmitting || !formik.dirty}
          variant="contained"
          fullWidth
          color="primary"
          size="large"
          loading={formik.isSubmitting}
          onClick={formik.submitForm}
        >
          {t('profile.action.save')}
        </SubmitButton>

        <DeleteButton
          variant="contrast-text"
          fullWidth
          color="error"
          size="large"
          onClick={() => setProfileRemoving(true)}
        >
          {t('profile.action.delete')}
        </DeleteButton>
        <Prompt
          open={profileRemoving}
          title={t('profile.title.confirmDeletion')}
          question={t('profile.hint.confirmDelete', { name: specificFullName }, true)}
          onClose='cancel'
          onEnter='confirm'
          actions={[
            {
              name: 'confirm',
              handler: deleteProfile,
              caption: t('common.action.delete'),
              style: {
                background: '#DE350B',
                color: '#FFFFFF',
                border: 'none',
              }
            },
            {
              name: 'cancel',
              handler: () => setProfileRemoving(false),
              caption: t('common.action.cancel'),
            }
          ]}
        />
      </BottomRow>
    </>
  );
};